import { useCallback, useEffect, useState } from "react";
import { RefreshCw, ShieldCheck, Lock } from "lucide-react";
import { toast } from "sonner";
import api from "@/lib/api";
import { useRequireAdmin } from "@/lib/useRequireAdmin";
import SharedNavigation from "@/components/SharedNavigation";
import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { AffiliatesTable, CommissionsTable, SkipsTable, LegacyReview } from "@/components/affiliate/AdminAffiliateTables";
import { PayoutsTable, SettingsForm } from "@/components/affiliate/AdminAffiliatePayouts";
import { usd } from "@/components/affiliate/affiliateFormat";

const Summary = ({ o }) => {
  const c = (k) => o.commissions[k] || { amount_cents: 0, count: 0, net_sales_cents: 0 };
  const netSales = ["pending", "available", "needs_review"].reduce((s, k) => s + c(k).net_sales_cents, 0);
  const items = [
    ["Affiliates (approved / pending)", `${o.affiliates.approved || 0} / ${o.affiliates.pending || 0}`, "approved"],
    ["Qualifying net sales", usd(netSales), "net-sales"],
    ["Pending commissions", usd(c("pending").amount_cents + c("needs_review").amount_cents), "pending"],
    ["Released commissions (lifetime)", usd(c("available").amount_cents), "available"],
    ["Owed to affiliates", usd(c("available").amount_cents - ((o.payouts.paid || {}).amount_cents || 0)), "owed"],
    ["Reversed (refunds)", `${c("reversed").count} · ${usd(c("reversed").amount_cents)}`, "reversed"],
    ["Paid out", usd((o.payouts.paid || {}).amount_cents || 0), "paid"],
  ];
  return (
    <div className="grid grid-cols-2 lg:grid-cols-3 gap-3" data-testid="admin-affiliate-summary">
      {items.map(([label, value, id]) => (
        <Card key={id} className="bg-black/40 border border-white/10 p-4">
          <p className="text-[11px] uppercase tracking-widest text-gray-400 font-bold">{label}</p>
          <p className="text-2xl font-black text-white mt-1" data-testid={`admin-affiliate-summary-${id}`}>{value}</p>
        </Card>
      ))}
    </div>
  );
};

const CONNECT_TONE = {
  live: "border-emerald-500/40 bg-emerald-500/10", test: "border-sky-500/40 bg-sky-500/10",
  unset: "border-gray-500/40 bg-gray-500/10", unknown: "border-rose-500/40 bg-rose-500/10",
};

const ConnectStatus = ({ sc, db }) => (
  <Card className={`border p-4 flex flex-col md:flex-row md:items-center gap-3 ${CONNECT_TONE[sc.mode]}`} data-testid="admin-affiliate-connect-status">
    <ShieldCheck className="w-6 h-6 text-white shrink-0" />
    <div className="flex-1">
      <p className="text-white font-bold" data-testid="admin-affiliate-connect-label">{sc.label}</p>
      <p className="text-xs text-gray-300 mt-0.5">{sc.detail}</p>
    </div>
    <div className="text-xs text-gray-300 md:text-right space-y-0.5">
      <p data-testid="admin-affiliate-connect-locked"><Lock className="w-3 h-3 inline mr-1" />Live affiliate payouts: locked</p>
      <p data-testid="admin-affiliate-connect-ready">{sc.affiliates_payout_ready} of {sc.approved_affiliates} approved affiliates have a ready payout account</p>
      <p className="text-gray-500" data-testid="admin-affiliate-data-source">Database: {db.database} · {db.users} members</p>
    </div>
  </Card>
);

export default function AdminAffiliatePage() {
  const { authChecked, currentUser } = useRequireAdmin({ allowAR: false, allowModerator: false });
  const [o, setO] = useState(null);
  const [lists, setLists] = useState({ affiliates: [], commissions: [], payouts: [], skips: [] });
  const [syncing, setSyncing] = useState(false);
  const [legacy, setLegacy] = useState(null);
  const loadLegacy = () => {
    api.get("/admin/affiliate/legacy-review").then((r) => setLegacy(r.data)).catch(() => toast.error("Could not load legacy review"));
  };

  const load = useCallback(async (syncNow = false) => {
    try {
      const ov = await api.get("/admin/affiliate/overview", { params: { sync_now: syncNow, recheck_refunds: syncNow } });
      const [a, c, p, s] = await Promise.all(["affiliates", "commissions", "payouts", "skips"].map((k) => api.get(`/admin/affiliate/${k}`)));
      setO(ov.data);
      setLists({ affiliates: a.data.affiliates, commissions: c.data.commissions, payouts: p.data.payouts, skips: s.data.skips });
      if (syncNow && ov.data.sync) toast.success(`Synced: ${ov.data.sync.created} new, ${ov.data.sync.released} released, ${ov.data.sync.reversed} reversed`);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not load affiliate program");
    }
  }, []);
  useEffect(() => { if (authChecked) load(true); }, [authChecked, load]);

  const run = async (fn, ok) => {
    try { await fn(); toast.success(ok); await load(); } catch (e) { toast.error(e?.response?.data?.detail || "Action failed"); }
  };
  const sync = async () => { setSyncing(true); await load(true); setSyncing(false); };

  if (!authChecked) return <div className="min-h-screen bg-[#08080c] text-gray-400 p-10">Checking access…</div>;
  return (
    <div className="min-h-screen bg-[#08080c]">
      <SharedNavigation currentUser={currentUser} />
      <div className="max-w-6xl mx-auto pt-24 pb-20 px-4 sm:px-6 space-y-5" data-testid="admin-affiliate-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div>
            <h1 className="text-4xl sm:text-5xl font-black text-white tracking-tight">Affiliate Program</h1>
            <p className="text-sm text-gray-400 mt-2">Founding affiliates · {o ? `${Math.round(o.settings.commission_rate * 100)}% of first membership payment net of tax & Stripe fees · ${o.settings.hold_days}-day hold` : "…"}</p>
          </div>
          <Button onClick={sync} disabled={syncing} variant="outline" data-testid="admin-affiliate-sync-btn">
            <RefreshCw className={`w-4 h-4 mr-2 ${syncing ? "animate-spin" : ""}`} />Sync with Stripe
          </Button>
        </div>
        {!o ? <p className="text-gray-500" data-testid="admin-affiliate-loading">Loading…</p> : (
          <>
            <ConnectStatus sc={o.stripe_connect} db={o.data_source} />
            <Summary o={o} />
            <Tabs defaultValue="affiliates">
              <TabsList className="flex-wrap h-auto">
                <TabsTrigger value="affiliates" data-testid="tab-affiliates">Affiliates</TabsTrigger>
                <TabsTrigger value="commissions" data-testid="tab-commissions">Commissions</TabsTrigger>
                <TabsTrigger value="payouts" data-testid="tab-payouts">Payouts</TabsTrigger>
                <TabsTrigger value="skips" data-testid="tab-skips">Excluded</TabsTrigger>
                <TabsTrigger value="legacy" data-testid="tab-legacy" onClick={loadLegacy}>Legacy review</TabsTrigger>
                <TabsTrigger value="settings" data-testid="tab-settings">Settings</TabsTrigger>
              </TabsList>
              <Card className="bg-black/40 border border-white/10 p-4 mt-3">
                <TabsContent value="affiliates"><AffiliatesTable rows={lists.affiliates} onStatus={(uid, status) => run(() => api.post(`/admin/affiliate/affiliates/${uid}/status`, { status }), `Affiliate ${status}`)} /></TabsContent>
                <TabsContent value="commissions"><CommissionsTable rows={lists.commissions} onAction={(id, body) => run(() => api.post(`/admin/affiliate/commissions/${id}/action`, body), "Commission updated")} /></TabsContent>
                <TabsContent value="payouts"><PayoutsTable rows={lists.payouts} connectMode={o.stripe_connect.mode} onAction={(id, body) => run(() => api.post(`/admin/affiliate/payouts/${id}/action`, body), "Payout updated")} /></TabsContent>
                <TabsContent value="skips"><SkipsTable rows={lists.skips} /></TabsContent>
                <TabsContent value="legacy"><LegacyReview data={legacy} /></TabsContent>
                <TabsContent value="settings"><SettingsForm settings={o.settings} onSave={(body) => run(() => api.patch("/admin/affiliate/settings", body), "Settings saved")} /></TabsContent>
              </Card>
            </Tabs>
          </>
        )}
      </div>
    </div>
  );
}
